////jc
import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  FormFeedback,
  Spinner,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, uploadFile, mediaUrl } from "../../../Utils/FileUpload";
import {
  Submit,
  EditPaymentSettingTitle,
  PaymentSettingTitle,
} from "../../../Utils/Constants";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CKEditor } from "@ckeditor/ckeditor5-react"; // Import CKEditor 5
import ClassicEditor from "@ckeditor/ckeditor5-build-classic"; // Import the CKEditor build
import { useNavigate, useLocation } from "react-router-dom";
import useCustomToasts from "../../../hooks/useCustomToast";

interface FormValues {
  gateway_name: string;
  secret_key: string;
  payment_key: string;
}
interface ErrorResponse {
  message: string;
}
export default function EditPaymentSetting() {
  const navigate = useNavigate();
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);
  const [loading, setLoading] = useState(true);
  const {
    handleSubmit,
    setValue,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      gateway_name: "",
      secret_key: "",
      payment_key: "",
    },
  });
  const location = useLocation();
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isPasswordVisible, setPasswordVisible] = useState(false);

  useEffect(() => {
    const fetchStaffsDetails = async () => {
      try {
        const token = localStorage.getItem("token");

        const result = await axios.post(
          `${baseUrl}payment_setting/viewbyid`,
          {
            id: location.state.id,
          },
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setValue("gateway_name", result.data.gateway_name);
        setValue("secret_key", result.data.secret_key);
        setValue("payment_key", result.data.payment_key);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching staffs details:", error);
      }
    };
    fetchStaffsDetails();
  }, [setValue]);

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);

    try {
      const editdata = {
        gateway_name: data.gateway_name,
        secret_key: data.secret_key,
        payment_key: data.payment_key,
        id: location.state.id,
      };
      // console.log(editdata, "editdata");
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${baseUrl}payment_setting/update`,
        editdata,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        SuccessToast("Data updated successfully", "/view_payment_setting");
      } else {
        ErrorToast("Failed to update");
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const errorResponse = axiosError.response?.data as ErrorResponse;
      const errorMessage =
        errorResponse?.message || "An unexpected error occurred";
      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 2000);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  // Prevent form submission on Enter key press
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  return (
    <>
      <Breadcrumbs
        pageTitle={EditPaymentSettingTitle}
        parent={PaymentSettingTitle}
        title={EditPaymentSettingTitle}
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody className="custom-input form-validation">
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row className="g-3">
                <Col xs={12}>
                  <Label htmlFor="gateway_name">Gateway Name</Label>
                  <Controller
                    name="gateway_name"
                    control={control}
                    rules={{ required: "Gateway Name is required" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="text"
                          id="gateway_name"
                          placeholder="Gateway name"
                          {...field}
                          invalid={!!errors.gateway_name}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.gateway_name ? "red" : "",
                          }}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.gateway_name && (
                          <FormFeedback>
                            {errors.gateway_name.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="secret_name">Secret Key</Label>
                  <Controller
                    name="secret_key"
                    control={control}
                    rules={{ required: "Secret Key is required" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="text"
                          id="secret_key"
                          placeholder="Secret key"
                          {...field}
                          invalid={!!errors.secret_key}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.secret_key ? "red" : "",
                          }}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.secret_key && (
                          <FormFeedback>
                            {errors.secret_key.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12}>
                  <Label htmlFor="payment_name">Payment Key</Label>
                  <Controller
                    name="payment_key"
                    control={control}
                    rules={{ required: "Payment Key is required" }}
                    render={({ field }) => (
                      <>
                        <Input
                          type="text"
                          id="payment_key"
                          placeholder="Payment key"
                          {...field}
                          invalid={!!errors.payment_key}
                          style={{
                            color: "var(--input-text-color)",
                            borderColor: errors.payment_key ? "red" : "",
                          }}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.payment_key && (
                          <FormFeedback>
                            {errors.payment_key.message}
                          </FormFeedback>
                        )}
                      </>
                    )}
                  />
                </Col>

                <Col xs={12} className="mt-2">
                  <Btn color="primary" disabled={isButtonDisabled}>
                    {Submit}
                  </Btn>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
      <ToastContainer
        toastStyle={{
          userSelect: "none",
        }}
      />
    </>
  );
}
